import { FC, MouseEvent, ReactNode } from "react";

import { Stack } from "@mui/material";

import { TableCellContainer } from "./TableCellContainer";

import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { IconElement } from "@/icons/types";

interface TableTwoLineCellProps {
  primary: string;
  primaryHref?: string;
  secondary?: ReactNode;
  secondaryIcon?: IconElement;
}

export const TableTwoLineCell: FC<TableTwoLineCellProps> = ({ primary, primaryHref, secondary, secondaryIcon }) => (
  <TableCellContainer layout="stacked" data-test="table-two-line-cell">
    {primaryHref ? (
      <DashboardLink href={primaryHref} vibe="brand" sx={{ minWidth: 0, maxWidth: "100%" }}>
        <DashboardTypography variant="body2" vibe="text-brand-moderate" noWrap component="span">
          {primary}
        </DashboardTypography>
      </DashboardLink>
    ) : (
      <DashboardTypography variant="body2" vibe="text-brand-moderate" noWrap sx={{ maxWidth: "100%" }}>
        {primary}
      </DashboardTypography>
    )}
    {secondary !== undefined && secondary !== null && (
      <Stack direction="row" alignItems="center" gap={0.5} minWidth={0} maxWidth="100%">
        {secondaryIcon && (
          <DashboardIcon size="extraSmall" vibe="text-neutral-supershy">
            {secondaryIcon}
          </DashboardIcon>
        )}
        <DashboardTypography variant={secondaryIcon ? "caption" : "body2"} vibe="text-neutral-supershy" noWrap>
          {secondary}
        </DashboardTypography>
      </Stack>
    )}
  </TableCellContainer>
);

interface TableIconTextCellProps {
  icon?: IconElement;
  value: string;
}

export const TableIconTextCell: FC<TableIconTextCellProps> = ({ icon, value }) => (
  <TableCellContainer layout="inline" data-test="table-icon-text-cell">
    {icon && (
      <DashboardIcon size="small" vibe="text-neutral-moderate">
        {icon}
      </DashboardIcon>
    )}
    <DashboardTypography variant="body2" vibe="text-neutral-moderate" noWrap sx={{ flex: "1 0 0", minWidth: 0 }}>
      {value}
    </DashboardTypography>
  </TableCellContainer>
);

interface TableQuickActionCellProps {
  icon: IconElement;
  label: string;
  isDisabled?: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const TableQuickActionCell: FC<TableQuickActionCellProps> = ({ icon, label, isDisabled, onClick }) => (
  <TableCellContainer layout="centered" data-test="table-quick-action-cell">
    <DashboardIconButton
      size="small"
      vibe="brand"
      sx={(theme) => ({ color: theme.palette.text.brand.intense })}
      slotProps={{ icon: { sx: (theme) => ({ width: theme.spacing(2.5), height: theme.spacing(2.5) }) } }}
      aria-label={label}
      disabled={isDisabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick(event);
      }}
    >
      {icon}
    </DashboardIconButton>
  </TableCellContainer>
);
